import { useState } from "react";

import { MenuItem, OverlayMenu } from "_/components/overlay-menu";
import { Table, ColumnDef } from "_/components/table";
import { TextLink, NeverUnderlineSpace } from "_/components/router";
import { Button } from "_/components/button";
import { Icon } from "_/components/icon";
import { MaterialsList } from "_/components/materials";
import { Modal, SubmitCancelButtons } from "_/components/modal";
import { DateTime, RelativeDateTime } from "_/components/date-time";
import { NumberBadge } from "_/components/number-badge";
import { t } from "@lingui/core/macro";

import { projectUrls } from "_/routes";
import { useDragAndDrop } from "_/hooks/useDragAndDrop";

import { QueueRow } from "./useQueueRows";
import * as S from "./styled";

type QueueTableProps = {
  rows: QueueRow[];
  canEdit: boolean;
  canDelete: boolean;
  compact?: boolean;
  showActions?: boolean;
  showDrag?: boolean;
  isReordering?: boolean;
  loading?: boolean;
  noDataLabel?: string;
  onDelete?: (entryId: string) => void;
  onMoveToTop?: (entry: QueueRow) => void;
  onReorder?: (
    entryId: string,
    bounds: { beforeId?: string; afterId?: string }
  ) => void;
};

const createMenuItems = (
  canEdit: boolean,
  onMoveToTop?: (entry: QueueRow) => void,
  row?: QueueRow
): MenuItem[] => {
  if (!canEdit || !row) return [];

  return [
    {
      render: t`components.queue.move-to-top`,
      onClick: () => onMoveToTop?.(row),
    },
  ];
};

const HandleColumnHeader = () => (
  <S.HandleColumnHeader>
    <Icon variant="Menu" />
  </S.HandleColumnHeader>
);

export const QueueTable = ({
  rows,
  canEdit,
  canDelete,
  compact = false,
  showActions = true,
  showDrag = true,
  isReordering = false,
  loading,
  noDataLabel,
  onDelete,
  onMoveToTop,
  onReorder,
}: QueueTableProps) => {
  const [entryToDelete, setEntryToDelete] = useState<QueueRow | null>(null);

  const dragAndDrop = useDragAndDrop<QueueRow>({
    rows,
    canEdit,
    isReordering,
    onReorder,
  });

  const getRowProps = (row: { original: QueueRow }) => {
    if (!showDrag || !canEdit || !onReorder) {
      return {};
    }

    return {
      as: S.DraggableRow,
      ...dragAndDrop.getItemProps(row.original),
    };
  };

  const baseColumns: (ColumnDef<QueueRow> | false)[] = [
    {
      id: "handle",
      header: HandleColumnHeader,
      cell: () =>
        showDrag && canEdit ? (
          <S.HandleCell>
            <Icon variant="Menu" />
          </S.HandleCell>
        ) : null,
      meta: { variant: "narrow" },
    },
    {
      id: "revision",
      header: t`components.queue.column-revision`,
      cell: ({ row }) => {
        const original = row.original;
        const revisionId = original.revisionId ?? original.entry.revisionId;
        const revisionLabel = revisionId ? revisionId.slice(0, 8) : "-";
        if (!original.projectId) {
          return revisionLabel;
        }
        return (
          <TextLink
            variant="default"
            to={projectUrls.prepare(original.projectId, original.revisionId)}
          >
            <span>{original.projectName}</span>
            <NeverUnderlineSpace />
            {original.revisionNumber != null ? (
              <NumberBadge number={original.revisionNumber} />
            ) : (
              "-"
            )}
          </TextLink>
        );
      },
    },
    !compact && {
      id: "materials",
      header: t`components.queue.column-materials`,
      cell: ({ row }) =>
        row.original.materials.length ? (
          <S.MaterialsCell>
            <MaterialsList materials={row.original.materials} />
          </S.MaterialsCell>
        ) : (
          "--"
        ),
    },
    {
      id: "submitted",
      header: t`components.queue.column-submitter`,
      cell: ({ row }) => (
        <span title={row.original.submittedBy?.email}>
          {row.original.submittedBy?.name ?? row.original.entry.submittedBy}
        </span>
      ),
    },
    !compact && {
      id: "age",
      header: t`components.queue.column-age`,
      cell: ({ row }) => (
        <S.SubmittedCell>
          <DateTime value={row.original.createdAt} />
          <S.SubmittedAgo>
            <RelativeDateTime value={row.original.createdAt} />
          </S.SubmittedAgo>
        </S.SubmittedCell>
      ),
    },
  ];

  const filteredColumns: ColumnDef<QueueRow>[] = baseColumns.filter(Boolean);

  if (showActions) {
    filteredColumns.push({
      id: "actions",
      header: t`common.actions`,
      cell: ({ row }) => (
        <S.ActionGroup>
          {canDelete && (
            <Button
              kind="secondary"
              size="small"
              iconOnly
              onClick={() => setEntryToDelete(row.original)}
            >
              <Icon variant="Delete" />
            </Button>
          )}
          {canEdit && (
            <OverlayMenu
              items={createMenuItems(canEdit, onMoveToTop, row.original)}
              target={
                <Button kind="secondary" size="small" iconOnly>
                  <Icon variant="MoreVert" />
                </Button>
              }
              placement="bottomEnd"
            />
          )}
        </S.ActionGroup>
      ),
      meta: { variant: "narrow" },
    });
  }

  const columns = filteredColumns;

  const handleConfirmRemove = () => {
    if (entryToDelete) {
      onDelete?.(entryToDelete.id);
    }
    setEntryToDelete(null);
  };

  return (
    <>
      <S.TableWrapper {...dragAndDrop.containerProps}>
        <Table
          columns={columns}
          data={rows}
          getRowProps={getRowProps}
          noDataLabel={noDataLabel}
          enableSorting={false}
          loading={loading}
        />
      </S.TableWrapper>
      <Modal
        open={!!entryToDelete}
        title={t`components.queue.remove-confirm-title`}
        onClose={() => setEntryToDelete(null)}
      >
        <p>{t`components.queue.remove-confirm-body`}</p>
        <SubmitCancelButtons
          submitButtonLabel={t`components.queue.remove-confirm-remove`}
          cancelButtonLabel={t`common.cancel`}
          onSubmit={handleConfirmRemove}
          onCancel={() => setEntryToDelete(null)}
          disableSubmit={!entryToDelete}
        />
      </Modal>
    </>
  );
};
